import React, { useEffect, useRef, useState } from 'react';
import './sellYourBusiness.css';

import buttonArrow from '../../assets/icons/button-arrow.svg';
import iconSpeed from '../../assets/sell-your-business/icon-speed-2.svg';
import iconConfidentiality from '../../assets/sell-your-business/icon-confidentiality-outer.svg';
import iconLegacy from '../../assets/sell-your-business/icon-legacy-1.svg';
import iconPeople from '../../assets/sell-your-business/icon-people-first.svg';
import iconSystems from '../../assets/sell-your-business/icon-better-systems.svg';
import iconTerms from '../../assets/sell-your-business/icon-flexible-terms.svg';
import cardImageSell from '../../assets/sell-your-business/card-sell.png';
import cardImagePartner from '../../assets/sell-your-business/card-partner.png';
import formBackground from '../../assets/sell-your-business/form-background.png';
import statementBackground from '../../assets/sell-your-business/statement-background.png';
import { sendSellInquiryEmail } from '../../utils/emailjs';

const processSteps = [
  {
    number: '01',
    title: 'Confidential Introduction',
    description: 'Provide an overview of your company and objectives during a discreet introductory discussion.',
  },
  {
    number: '02',
    title: 'Tailored Proposal',
    description: 'Our team analyzes your business\'s potential and provides a transparent, strategic path forward aligned with your goals.',
  },
  {
    number: '03',
    title: 'Partnership & Execution',
    description: 'We finalize the transaction with certainty, honoring your achievements while deploying scalable infrastructure for future success.',
  },
];

const options = [
  {
    eyebrow: 'SELL YOUR BUSINESS',
    title: 'Capitalize on Your Business\'s True Worth',
    description: 'If you are ready to move on to your next venture or simply want to exit with peace of mind, we can take the wheel. We buy businesses and use our resources and expertise to keep them operating smoothly and sustainably. You get a clean break, fair value, and the satisfaction of knowing your company is in safe hands.',
    cta: 'Get Your Valuation',
    image: cardImageSell,
    cardClass: 'kw__sell-option-card--sell',
  },
  {
    eyebrow: 'PARTNER WITH US',
    title: 'Let\'s Build Together & Become a Strategic Partner',
    description: 'If you still believe in your business but need extra strength to grow, let\'s join forces. We invest together, share the workload, and build a brighter future as true partners. When we combine your ideas with our strength, both sides win.',
    cta: 'Let\'s Collaborate',
    image: cardImagePartner,
    cardClass: '',
  },
];

const benefits = [
  {
    icon: iconSpeed,
    title: 'Speed & Certainty',
    description: 'We move from intent to close without delays. Traditional buyers stall in lengthy diligence or shop around for third-party financing. With Kothawala Group, you get direct decision-makers, a streamlined review process, and absolute closing certainty on agreed terms.',
  },
  {
    icon: iconConfidentiality,
    title: 'Confidentiality',
    description: 'Your brand, clients, and employees are fully protected. We operate under strict non-disclosure frameworks and discreet protocols. Your market presence and internal stability remain uninterrupted throughout the entire acquisition process.',
  },
  {
    icon: iconLegacy,
    title: 'Clean Exits & Legacy Care',
    description: 'We honor what you\'ve built while engineering the next phase of growth. Whether you want a complete, clean exit or wish to roll equity and stay involved, we handle handoffs with total professionalism.',
  },
  {
    icon: iconPeople,
    title: 'People-First',
    description: 'We don\'t gut teams; we build the operating machine behind them. Your employees are vital to ongoing success. We preserve existing talent, provide growth opportunities, and plug in expert leadership and back-office support to help them thrive.',
  },
  {
    icon: iconSystems,
    title: 'Better Systems',
    description: 'We absorb operational complexity so the business can scale. Our team integrates deeply into the business\'s infrastructure to solve back-office bottlenecks, streamline reporting, and eliminate inefficiencies without disrupting daily revenue.',
  },
  {
    icon: iconTerms,
    title: 'Flexible Terms',
    description: 'Tailored terms aligned with your personal and financial goals. We offer flexible deal structures including full buyouts, phased earn-outs, or growth partnerships designed to maximize total value and achieve your desired outcome.',
  },
];

const SellYourBusiness = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    nationality: '',
    companyName: '',
    industry: '',
    location: '',
    companyWebsite: '',
    yearsOperating: '',
    summary: '',
  });
  const inquiryRef = useRef(null);
  const firstInquiryFieldRef = useRef(null);

  const scrollToInquirySection = () => {
    if (!inquiryRef.current) {
      return;
    }

    const navbar = document.querySelector('.kw__navbar');
    const navbarOffset = navbar ? navbar.getBoundingClientRect().height : 0;
    const y = inquiryRef.current.getBoundingClientRect().top + window.scrollY - navbarOffset - 24;

    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });

    window.history.replaceState(null, '', '#kw-sell-inquiry');
  };

  useEffect(() => {
    if (window.location.hash === '#kw-sell-inquiry') {
      requestAnimationFrame(() => {
        scrollToInquirySection();
      });
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await sendSellInquiryEmail(formData);
    setFormData({
      name: '',
      email: '',
      phone: '',
      nationality: '',
      companyName: '',
      industry: '',
      location: '',
      companyWebsite: '',
      yearsOperating: '',
      summary: '',
    });
  };

  const handleScrollToInquiry = () => {
    scrollToInquirySection();

    // Move keyboard focus to the first field after scrolling for accessibility.
    requestAnimationFrame(() => {
      if (firstInquiryFieldRef.current) {
        firstInquiryFieldRef.current.focus();
      }
    });
  };

  return (
    <div className="kw__sell-page">
      <section className="kw__sell-hero kw__sell-section">
        <p className="kw__sell-hero-label">The Real Solution for Struggling Businesses.</p>
        <h1 className="kw__sell-hero-title">Sell Your Business or Partner with Us for Real Results</h1>
        <p className="kw__sell-hero-copy">
          At Kothawala Group, we look beyond where your business is today to focus on what it can become. We partner with ambitious business owners
          ready for their next chapter offering clear options to move forward with confidence, whether that means a complete exit, growth capital, or
          strategic operational leadership to scale. When we see potential, we move decisively to build lasting value.
        </p>
      </section>

      <section className="kw__sell-process kw__sell-section">
        <h2 className="kw__sell-section-title">Our Process</h2>
        <div className="kw__sell-process-grid">
          {processSteps.map((step, index) => (
            <article key={step.number} className="kw__sell-process-card">
              <div className="kw__sell-process-number-row">
                <span className="kw__sell-process-number">{step.number}</span>
                {index < processSteps.length - 1 && <span className="kw__sell-process-line" aria-hidden="true" />}
              </div>
              <h3 className="kw__sell-process-title">{step.title}</h3>
              <p className="kw__sell-process-copy">{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="kw__sell-options kw__sell-section">
        {options.map((option) => (
          <article key={option.eyebrow} className={`kw__sell-option-card ${option.cardClass || ''}`} style={{ backgroundImage: `url(${option.image})` }}>
            <div className="kw__sell-option-panel">
              <p className="kw__sell-option-eyebrow">{option.eyebrow}</p>
              <h3 className="kw__sell-option-title">{option.title}</h3>
              <p className="kw__sell-option-copy">{option.description}</p>
            </div>
            <button
              type="button"
              className="kw__sell-option-button"
              onClick={handleScrollToInquiry}
            >
              <span>{option.cta}</span>
              <img src={buttonArrow} alt="" />
            </button>
          </article>
        ))}
      </section>

      <section className="kw__sell-benefits kw__sell-section">
        <h2 className="kw__sell-section-title">Why Sell to Kothawala Group?</h2>
        <p className="kw__sell-benefits-intro">
          Selling or scaling a business is a major milestone. We provide founders and business owners with the capital, operational precision, and
          execution certainty needed for a seamless transition or accelerated growth.
        </p>
        <div className="kw__sell-benefits-grid">
          {benefits.map((benefit) => (
            <article key={benefit.title} className="kw__sell-benefit-card">
              <img className="kw__sell-benefit-icon" src={benefit.icon} alt="" aria-hidden="true" />
              <h3 className="kw__sell-benefit-title">{benefit.title}</h3>
              <p className="kw__sell-benefit-copy">{benefit.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="kw__sell-statement" style={{ backgroundImage: `linear-gradient(rgba(15, 15, 15, 0.38), rgba(15, 15, 15, 0.38)), url(${statementBackground})` }}>
        <div className="kw__sell-statement-overlay" />
        <p className="kw__sell-statement-line">We Don&apos;t Just Invest.</p>
        <p className="kw__sell-statement-line kw__sell-statement-line--offset">We Take Over Chaos.</p>
      </section>

      <section id="kw-sell-inquiry" className="kw__sell-inquiry" ref={inquiryRef}>
        <div className="kw__sell-inquiry-bg">
          <img src={formBackground} alt="" aria-hidden="true" />
        </div>
        <div className="kw__sell-inquiry-content kw__sell-section">
          <p className="kw__sell-inquiry-label">Start a Conversation with Kothawala Group</p>
          <h2 className="kw__sell-inquiry-title">Explore Selling & Strategic Partnership Inquiry Form</h2>

          <form id="kw-sell-inquiry-form" className="kw__sell-form" onSubmit={handleSubmit}>
            <div className="kw__sell-form-column">
              <input ref={firstInquiryFieldRef} name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} />
              <input name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} />
              <input name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} />
              <input name="nationality" placeholder="Nationality" value={formData.nationality} onChange={handleChange} />
              <textarea name="summary" placeholder="Tell us briefly about your business and what you are looking for" value={formData.summary} onChange={handleChange} />
            </div>

            <div className="kw__sell-form-column">
              <input name="companyName" placeholder="Company Name" value={formData.companyName} onChange={handleChange} />
              <input name="industry" placeholder="Industry" value={formData.industry} onChange={handleChange} />
              <input name="location" placeholder="Location" value={formData.location} onChange={handleChange} />
              <input name="companyWebsite" placeholder="Company Website" value={formData.companyWebsite} onChange={handleChange} />
              <textarea name="yearsOperating" placeholder="How long has the business been operating?" value={formData.yearsOperating} onChange={handleChange} />
            </div>
          </form>

          <p className="kw__sell-inquiry-note">
            Submission of this form is for evaluation purposes only and does not establish a legal or financial commitment between you and Kothawala
            Group.
          </p>
          <button type="submit" form="kw-sell-inquiry-form" className="kw__sell-inquiry-submit">
            <span>Submit</span>
            <img src={buttonArrow} alt="" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default SellYourBusiness;
